import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateOrganizationInput,
  AddMemberInput,
} from "@shared/validation/organization.schema";
import { INSTITUTIONS_ENABLED } from "@shared/constants/features";
import { apiClient, ApiError } from "./client";

export type OrgRole = "org_admin" | "staff";
export type OrgKind = "hospital" | "school" | "other";

export interface OrganizationSummary {
  id: string;
  name: string;
  slug: string;
  kind: OrgKind;
  /** The current user's role in this org. */
  role: OrgRole;
  createdAt: string;
}

export interface OrgMember {
  id: string;
  userId: string;
  email: string;
  fullName: string | null;
  role: OrgRole;
  createdAt: string;
}

const ORGS_KEY = ["orgs"] as const;
const orgMembersKey = (orgId: string) => ["orgs", orgId, "members"] as const;
const AUTH_USER_KEY = ["auth", "user"] as const;
const CHILDREN_KEY = ["children"] as const;

export function useMyOrgs() {
  return useQuery<OrganizationSummary[], ApiError>({
    queryKey: ORGS_KEY,
    queryFn: () => apiClient<OrganizationSummary[]>("/api/orgs"),
    // With the institutional layer off, /api/orgs is not mounted. Disabling the
    // query here (rather than at each call site) stops every page load firing a
    // request that would 404: consumers already read `orgs?.find(…) ?? null`,
    // so `undefined` degrades to the family path on its own.
    enabled: INSTITUTIONS_ENABLED,
  });
}

export function useCreateOrg() {
  const queryClient = useQueryClient();
  return useMutation<OrganizationSummary, ApiError, CreateOrganizationInput>({
    mutationFn: (vars) =>
      apiClient<OrganizationSummary>("/api/orgs", {
        method: "POST",
        body: JSON.stringify(vars),
      }),
    onSuccess: () => {
      // Creating an org auto-switches into it server-side.
      queryClient.invalidateQueries({ queryKey: ORGS_KEY });
      queryClient.invalidateQueries({ queryKey: AUTH_USER_KEY });
      queryClient.invalidateQueries({ queryKey: CHILDREN_KEY });
    },
  });
}

export function useSwitchOrg() {
  const queryClient = useQueryClient();
  return useMutation<{ organizationId: string | null }, ApiError, string | null>({
    mutationFn: (organizationId) =>
      apiClient<{ organizationId: string | null }>("/api/orgs/switch", {
        method: "POST",
        body: JSON.stringify({ organizationId }),
      }),
    onSuccess: () => {
      // Context changed — the current user DTO and the (org-scoped) record list
      // both depend on it.
      queryClient.invalidateQueries({ queryKey: AUTH_USER_KEY });
      queryClient.invalidateQueries({ queryKey: CHILDREN_KEY });
    },
  });
}

export function useOrgMembers(orgId: string | undefined) {
  return useQuery<OrgMember[], ApiError>({
    queryKey: orgId ? orgMembersKey(orgId) : ["orgs", "__none__", "members"],
    queryFn: () => {
      if (!orgId) throw new Error("useOrgMembers: orgId required");
      return apiClient<OrgMember[]>(`/api/orgs/${orgId}/members`);
    },
    enabled: Boolean(orgId),
  });
}

export function useAddMember(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation<OrgMember, ApiError, AddMemberInput>({
    mutationFn: (vars) =>
      apiClient<OrgMember>(`/api/orgs/${orgId}/members`, {
        method: "POST",
        body: JSON.stringify(vars),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: orgMembersKey(orgId) }),
  });
}

export function useUpdateMemberRole(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation<OrgMember, ApiError, { memberId: string; role: OrgRole }>({
    mutationFn: ({ memberId, role }) =>
      apiClient<OrgMember>(`/api/orgs/${orgId}/members/${memberId}`, {
        method: "PATCH",
        body: JSON.stringify({ role }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: orgMembersKey(orgId) }),
  });
}

export function useRemoveMember(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ removed: true }, ApiError, string>({
    mutationFn: (memberId) =>
      apiClient<{ removed: true }>(`/api/orgs/${orgId}/members/${memberId}`, {
        method: "DELETE",
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: orgMembersKey(orgId) }),
  });
}
